import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useRecoilState } from 'recoil';

import { API_URL } from 'constant';
import { useNetwork, useNotification } from 'hooks';
import { documentTypeRegex } from 'utils';
import { ComplexSettingFormState } from 'views/pipeline';
import './node-document-upload.scss';

interface INodeDoc {
	docId: string;
	name: string;
	fileUrl: string;
	size: number;
}

interface IProps {
	nodeId: string;
}

const MAX_SIZE = 10000000; // 10 MB, same limit as the QR-flow uploader

/**
 * Attach the review documents DIRECTLY on the Document Review (proofReading) node, with multi-file
 * support. Docs are stored on `ComplexSettingFormState[nodeId].documents`; publish serializes that
 * into the node's `metadata.documents` (via getMetData), which the backend already reads
 * (proofDocsForNode / assembleSessionArtifacts). Replaces collecting docs at QR-generation time.
 */
export const NodeDocumentUpload: FC<IProps> = ({ nodeId }) => {
	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);
	const { formDataWithAxios: uploadProofDoc, remove: deleteProofDoc } =
		useNetwork();
	const { errorNotification, successNotification } = useNotification();

	const documents: INodeDoc[] = useMemo(
		() => complexSettingForm?.[nodeId]?.documents ?? [],
		[complexSettingForm, nodeId]
	);

	const setDocuments = useCallback(
		(updater: (prev: INodeDoc[]) => INodeDoc[]) => {
			setComplexSettingForm((prev: any) => {
				const next = structuredClone(prev ?? {});
				if (!next[nodeId]) next[nodeId] = {};
				next[nodeId].documents = updater(next[nodeId].documents ?? []);
				return next;
			});
		},
		[nodeId, setComplexSettingForm]
	);

	const handleFiles = useCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			e.target.value = '';
			for (const file of files) {
				if (file.size > MAX_SIZE) {
					errorNotification(`${file.name}: exceeds the 10MB limit.`);
					continue;
				}
				if (!documentTypeRegex.test(file.type)) {
					errorNotification(`${file.name}: only pdf, doc and docx are supported.`);
					continue;
				}
				const payload = new FormData();
				payload.append('doc', file);
				payload.append('nodeId', nodeId);
				try {
					const response = await uploadProofDoc(
						`${'/url' + API_URL.UPLOAD_PROOF}`,
						payload
					);
					const { docId, url } = response?.data ?? {};
					if (docId) {
						setDocuments(prev => [
							...prev,
							{ docId, name: file.name, fileUrl: url ?? '', size: file.size },
						]);
						successNotification(`${file.name} uploaded.`);
					} else {
						errorNotification(`${file.name}: upload failed.`);
					}
				} catch {
					errorNotification(`${file.name}: upload failed.`);
				}
			}
		},
		[nodeId, uploadProofDoc, setDocuments, errorNotification, successNotification]
	);

	const handleRemove = useCallback(
		async (docId: string) => {
			setDocuments(prev => prev.filter(d => d.docId !== docId));
			try {
				await deleteProofDoc(`${API_URL.UPLOAD_PROOF}/${docId}`);
			} catch {
				// best-effort: the doc is already removed from the node; ignore storage errors
			}
		},
		[deleteProofDoc, setDocuments]
	);

	return (
		<div className="node-doc-upload" onClick={e => e.stopPropagation()}>
			{!!documents.length && (
				<div className="node-doc-upload__list">
					{documents.map(doc => (
						<div className="node-doc-upload__item" key={doc.docId}>
							<span className="node-doc-upload__name" title={doc.name}>
								{doc.name}
							</span>
							<button
								type="button"
								className="node-doc-upload__remove"
								onClick={() => handleRemove(doc.docId)}
							>
								Remove
							</button>
						</div>
					))}
				</div>
			)}
			<label className="node-doc-upload__add">
				<input
					type="file"
					multiple
					accept=".pdf,.doc,.docx"
					onChange={handleFiles}
				/>
				<span>+ Add document(s)</span>
			</label>
		</div>
	);
};
